/**
 * Utility to convert automation data sources to AvailableVariable format
 * for use with the context-aware merge variable dropdown.
 */

import { VariableFieldType, VariableTypeKey } from '@supply-mover-2-0/shared';
import type { AvailableVariable } from '../../services/templateContextAnalyzer';

/**
 * Data source field as returned by getAvailableDataSources
 */
interface DataSourceField {
	key: string;
	label: string;
	type: string;
	example?: string;
	displayKey?: string;
	arrayType?: VariableTypeKey;
	objectType?: VariableTypeKey;
}

/**
 * Data source category as returned by getAvailableDataSources
 */
interface DataSourceCategory {
	category: string;
	icon?: unknown;
	fields: Array<DataSourceField>;
}

/**
 * Convert a type string to VariableFieldType enum
 */
function parseFieldType(typeStr: string): VariableFieldType {
	const normalized = typeStr.toLowerCase();

	switch (normalized) {
		case 'string':
		case 'text':
			return VariableFieldType.STRING;
		case 'number':
		case 'integer':
		case 'float':
			return VariableFieldType.NUMBER;
		case 'boolean':
		case 'bool':
			return VariableFieldType.BOOLEAN;
		case 'date':
		case 'datetime':
			return VariableFieldType.DATE;
		case 'currency':
		case 'money':
			return VariableFieldType.CURRENCY;
		case 'email':
			return VariableFieldType.EMAIL;
		case 'phone':
			return VariableFieldType.PHONE;
		case 'array':
			return VariableFieldType.ARRAY;
		case 'object':
			return VariableFieldType.OBJECT;
		default:
			return VariableFieldType.STRING;
	}
}

/**
 * Convert a single data source field to an AvailableVariable
 */
function fieldToAvailableVariable(field: DataSourceField): AvailableVariable {
	const fieldType = parseFieldType(field.type);
	const isArray = fieldType === VariableFieldType.ARRAY;
	const isObject = fieldType === VariableFieldType.OBJECT;

	return {
		key: field.key,
		label: field.label,
		type: fieldType,
		isArray,
		isObject,
		arrayType: field.arrayType,
		objectType: field.objectType,
		description:
			field.example != null ? `Example: ${field.example}` : undefined,
	};
}

/**
 * Convert all data sources to a flat array of AvailableVariable
 *
 * @param dataSources - Array of data source categories from getAvailableDataSources()
 * @returns Flat array of AvailableVariable for context-aware merge variable dropdown
 */
export function convertDataSourcesToVariables(
	dataSources: Array<DataSourceCategory>,
): Array<AvailableVariable> {
	const variables: Array<AvailableVariable> = [];

	for (const category of dataSources) {
		for (const field of category.fields) {
			variables.push(fieldToAvailableVariable(field));
		}
	}

	return variables;
}

/**
 * Convert data sources grouped by category
 * Useful for displaying variables in a categorized list
 *
 * @param dataSources - Array of data source categories
 * @returns Map of category name to variables in that category
 */
export function convertDataSourcesToCategorizedVariables(
	dataSources: Array<DataSourceCategory>,
): Map<string, Array<AvailableVariable>> {
	const categorized = new Map<string, Array<AvailableVariable>>();

	for (const category of dataSources) {
		const variables = category.fields.map((field) =>
			fieldToAvailableVariable(field),
		);
		categorized.set(category.category, variables);
	}

	return categorized;
}
